import { useState, useEffect } from 'react';
import api from '../api';
import toast from 'react-hot-toast';

const EMPTY = { name: '', email: '', password: '', company: '', report_email: '', report_frequency: 'daily', report_enabled: false };

export default function ClientManagement() {
  const [clients, setClients] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [quotaModal, setQuotaModal] = useState(false);
  const [quotaClient, setQuotaClient] = useState(null);
  const [quota, setQuota] = useState({ max_cameras:10, max_subusers:5, report_enabled:true, notes:'' });

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    const res = await api.get('/clients');
    setClients(res.data);
  }

  function openCreate() {
    setEditId(null);
    setForm(EMPTY);
    setModal(true);
  }

  function openEdit(c) {
    setEditId(c.id);
    setForm({ ...c, password: '' });
    setModal(true);
  }

  async function handleSave(e) {
    e.preventDefault();
    setLoading(true);
    try {
      if (editId) {
        await api.put(`/clients/${editId}`, form);
        toast.success('Klientas atnaujintas');
      } else {
        await api.post('/clients', form);
        toast.success('Klientas sukurtas');
      }
      setModal(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Klaida');
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(id, name) {
    if (!window.confirm(`Ištrinti klientą "${name}"? Bus ištrinti visi jo duomenys ir kameros!`)) return;
    await api.delete(`/clients/${id}`);
    toast.success('Klientas ištrintas');
    loadData();
  }

  const freqLabel = { hourly: 'Kas valandą', daily: 'Kasdien', weekly: 'Kas savaitę', monthly: 'Kas mėnesį' };

  async function openQuota(client) {
    setQuotaClient(client);
    const r = await api.get(`/quotas/${client.id}`);
    setQuota({ ...r.data, report_enabled: !!r.data.report_enabled });
    setQuotaModal(true);
  }

  async function saveQuota(e) {
    e.preventDefault();
    await api.put(`/quotas/${quotaClient.id}`, quota);
    toast.success('Kvota išsaugota');
    setQuotaModal(false);
  }

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div className="page-title">Klientų valdymas</div>
          <div className="page-subtitle">Kurkite ir valdykite klientų paskyras</div>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>+ Naujas klientas</button>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Klientas</th>
                <th>El. paštas</th>
                <th>Kameros</th>
                <th>Ataskaitos</th>
                <th>Registruotas</th>
                <th>Veiksmai</th>
              </tr>
            </thead>
            <tbody>
              {clients.map(c => (
                <tr key={c.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{c.company || c.name}</div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{c.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.company}</div>
                  </td>
                  <td>{c.email}</td>
                  <td><span className="badge badge-info">📷 {c.camera_count}</span></td>
                  <td>
                    {c.report_enabled ? (
                      <span className="badge badge-success">✅ {freqLabel[c.report_frequency]}</span>
                    ) : (
                      <span className="badge badge-danger">⛔ Išjungta</span>
                    )}
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{c.report_email}</div>
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {new Date(c.created_at).toLocaleDateString('lt-LT')}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-sm btn-secondary" onClick={() => openEdit(c)}>✏️ Redaguoti</button>
                      <button className="btn btn-sm btn-secondary" onClick={() => openQuota(c)} title="Kvotos">📊</button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(c.id, c.name)}>🗑️</button>
                    </div>
                  </td>
                </tr>
              ))}
              {!clients.length && (
                <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40 }}>Klientų nerasta</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <div className="modal-title">{editId ? 'Redaguoti klientą' : 'Naujas klientas'}</div>
              <button className="modal-close" onClick={() => setModal(false)}>✕</button>
            </div>

            <form onSubmit={handleSave}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Vardas, pavardė *</label>
                  <input className="form-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Įmonė</label>
                  <input className="form-input" value={form.company} onChange={e => setForm({...form, company: e.target.value})} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">El. paštas (prisijungimui) *</label>
                  <input className="form-input" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Slaptažodis {editId ? '(tuščia = nekeisti)' : '*'}</label>
                  <input className="form-input" type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} required={!editId} />
                </div>
              </div>

              <div className="divider" />
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 16, color: 'var(--text-muted)' }}>📧 Automatinių ataskaitų nustatymai</div>

              <div className="form-group">
                <label className="form-label">Ataskaitų el. paštas</label>
                <input className="form-input" type="email" value={form.report_email} onChange={e => setForm({...form, report_email: e.target.value})} placeholder="ataskaitos@imone.lt" />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Siuntimo periodiškumas</label>
                  <select className="form-select" value={form.report_frequency} onChange={e => setForm({...form, report_frequency: e.target.value})}>
                    <option value="hourly">Kas valandą</option>
                    <option value="daily">Kasdien (07:00)</option>
                    <option value="weekly">Kas savaitę (pirmadienį)</option>
                    <option value="monthly">Kas mėnesį (1 d.)</option>
                  </select>
                </div>
                <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', paddingBottom: 10 }}>
                    <input type="checkbox" checked={form.report_enabled} onChange={e => setForm({...form, report_enabled: e.target.checked})} />
                    <span className="form-label" style={{ margin: 0 }}>Automatinis siuntimas įjungtas</span>
                  </label>
                </div>
              </div>

              <div className="divider" />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setModal(false)}>Atšaukti</button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? <span className="loading-spinner" /> : editId ? 'Išsaugoti' : 'Sukurti'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    {quotaModal && quotaClient && (
      <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setQuotaModal(false)}>
        <div className="modal">
          <div className="modal-header">
            <div className="modal-title">📊 Kvotos: {quotaClient.company || quotaClient.name}</div>
            <button className="modal-close" onClick={() => setQuotaModal(false)}>✕</button>
          </div>
          <form onSubmit={saveQuota}>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Maks. kamerų skaičius</label>
                <input className="form-input" type="number" min="0" value={quota.max_cameras} onChange={e => setQuota({...quota,max_cameras:parseInt(e.target.value)})} />
              </div>
              <div className="form-group">
                <label className="form-label">Maks. sub-vartotojų</label>
                <input className="form-input" type="number" min="0" value={quota.max_subusers} onChange={e => setQuota({...quota,max_subusers:parseInt(e.target.value)})} />
              </div>
            </div>
            <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer', padding:'10px 14px', background:'var(--bg)', borderRadius:8, border:'1px solid var(--border)', marginBottom:12 }}>
              <input type="checkbox" checked={quota.report_enabled} onChange={e => setQuota({...quota,report_enabled:e.target.checked})} style={{ width:16,height:16 }} />
              <span style={{ fontSize:13, fontWeight:600 }}>✅ Automatinės ataskaitos įjungtos</span>
            </label>
            <div className="form-group">
              <label className="form-label">Pastabos (vidaus)</label>
              <textarea className="form-input" value={quota.notes||''} onChange={e => setQuota({...quota,notes:e.target.value})} rows={2} placeholder="Vidaus pastabos apie klientą..." />
            </div>
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button type="button" className="btn btn-secondary" onClick={() => setQuotaModal(false)}>Atšaukti</button>
              <button type="submit" className="btn btn-primary">💾 Išsaugoti</button>
            </div>
          </form>
        </div>
      </div>
    )}
    </div>
  );
}
